import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { Phone, Mail, MapPin, Clock, User, Calendar, MessageSquare, CheckCircle, Star, ArrowRight } from 'lucide-react';
import UniversalFormWrapper from '../components/UniversalFormWrapper';

interface ConsultationFormData {
  name: string;
  email: string;
  phone: string;
  course: string;
  message: string;
  referralId?: string;
  referralName?: string;
  referralPhone?: string;
}

const ConsultancyPage: React.FC = () => {
  const [isSubmitted] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm<ConsultationFormData>();

  useEffect(() => {
    if (window.location.hash === '#consultation-form') {
      document.getElementById('consultation-form')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const onSubmit = (data: ConsultationFormData) => {
    // Construct mailto link
    const subject = encodeURIComponent('Free Consultation Request');
    const body = encodeURIComponent(
      `Full Name: ${data.name}\n` +
      `Email: ${data.email}\n` +
      `Phone: ${data.phone}\n` +
      `Course of Interest: ${data.course}\n` +
      `Message: ${data.message || ''}\n` +
      (data.referralId ? `Referral ID: ${data.referralId}\n` : '') +
      (data.referralName ? `Referral Name: ${data.referralName}\n` : '') +
      (data.referralPhone ? `Referral Phone: ${data.referralPhone}\n` : '')
    );
    const mailtoLink = `mailto:infonammasewa@gmail.com?subject=${subject}&body=${body}`;
    window.location.href = mailtoLink;
  };

  const services = [
    {
      title: "Career Counseling",
      description: "Get personalized guidance on choosing the right career path based on your interests and strengths.",
      icon: <User className="h-8 w-8" />,
      features: ["Aptitude Assessment", "Career Mapping", "Goal Setting", "Progress Tracking"]
    },
    {
      title: "College Selection",
      description: "Find the perfect college that matches your academic profile, budget, and career goals.",
      icon: <Calendar className="h-8 w-8" />,
      features: ["College Rankings", "Admission Requirements", "Fee Structure", "Placement Records"]
    },
    {
      title: "Admission Guidance",
      description: "Complete support through the admission process from application to enrollment.",
      icon: <MessageSquare className="h-8 w-8" />,
      features: ["Application Review", "Document Preparation", "Interview Coaching", "Deadline Management"]
    }
  ];

  // Optimized animation variants for better performance
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.3 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const testimonials = [
    {
      name: "Priya Patel",
      course: "MBBS",
      text: "The guidance I received was exceptional. They helped me secure admission to my dream medical college!",
      rating: 5
    },
    {
      name: "Amit Kumar",
      course: "Engineering",
      text: "Their expertise in engineering admissions is unmatched. Highly recommend their services!",
      rating: 5
    },
    {
      name: "Sneha Reddy",
      course: "Scholarship",
      text: "Got a scholarship worth ₹5 lakhs with their help. Forever grateful!",
      rating: 5
    }
  ];

  return (
    <UniversalFormWrapper 
      formType="consultation" 
      source="ConsultancyPage" 
      priority="high"
    >
      <div className="min-h-screen relative font-sans antialiased bg-gradient-to-br from-gray-900 via-blue-900 to-black text-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-gray-900 via-blue-900 to-black text-white py-16 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            {...fadeInUp}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-extrabold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 tracking-tight">
              Expert Educational Services
            </h1>
            <p className="text-2xl md:text-3xl mb-8 text-cyan-200">
              Get personalized guidance from experienced education consultants
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#consultation-form"
                className="bg-cyan-400/10 text-pink-400 px-8 py-3 rounded-lg font-semibold hover:scale-105 hover:ring-2 hover:ring-cyan-400 transition inline-flex items-center justify-center backdrop-blur-lg border border-cyan-400/10 shadow-md"
              >
                Book Free Consultation
                <ArrowRight className="ml-2 h-5 w-5 text-pink-400" />
              </a>
              <a
                href="/premium-services"
                className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-300 px-8 py-3 rounded-lg font-semibold hover:scale-105 hover:ring-2 hover:ring-purple-400 transition inline-flex items-center justify-center backdrop-blur-lg border border-purple-400/30 shadow-md"
              >
                Premium Services
                <ArrowRight className="ml-2 h-5 w-5 text-purple-300" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            {...fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 tracking-tight">
              Our Services
            </h2>
            <p className="text-lg max-w-2xl mx-auto text-cyan-200">
              Comprehensive educational guidance tailored to your unique needs and aspirations
            </p>
          </motion.div>

          <motion.div 
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="backdrop-blur-lg bg-cyan-400/5 border border-cyan-400/10 rounded-xl shadow-md p-5 hover:scale-105 hover:ring-2 hover:ring-cyan-400 transition cursor-pointer"
              >
                <div className="mb-4 text-cyan-400 group-hover:text-pink-400">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2 text-cyan-100">
                  {service.title}
                </h3>
                <p className="text-cyan-200 mb-4">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-cyan-200 font-bold">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Consultation Form */}
      <section id="consultation-form" className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            {...fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 tracking-tight">
              Book Your Free Consultation
            </h2>
            <p className="text-lg max-w-2xl mx-auto text-cyan-200">
              Fill out the form below and our experts will get back to you within 24 hours
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <motion.div
              {...fadeInUp}
            >
              <h3 className="text-xl font-semibold mb-6 text-pink-400">Get in Touch</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Phone className="h-5 w-5 text-blue-600" />
                <span className="text-pink-400">+91 77086 77132</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-blue-600" />
                <span className="text-pink-400">infonammasewa@gmail.com</span>
                </div>
                <div className="flex items-center space-x-3">
                  <MapPin className="h-5 w-5 text-blue-600" />
                <span className="text-pink-400">SEWA head office, Manickampalayam, Tiruchengode, Namakkal, Tamil Nadu, India</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="h-5 w-5 text-blue-600" />
                <span className="text-pink-400">Mon - Sat: 9:00 AM - 6:00 PM</span>
                </div>
              </div>

              <div className="mt-8">
                <h4 className="font-semibold mb-4 text-pink-400">What Our Students Say</h4>
                <div className="space-y-4">
                  {testimonials.map((testimonial, index) => (
                    <div key={index} className="border-l-4 border-blue-600 pl-4">
                      <p className="text-sm text-pink-400 italic">"{testimonial.text}"</p>
                      <p className="text-sm font-medium text-pink-400 mt-1">
                        - {testimonial.name}, {testimonial.course}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              {...fadeInUp}
              className="backdrop-blur-lg bg-cyan-400/5 border border-cyan-400/10 rounded-xl shadow-md p-8 hover:scale-105 hover:ring-2 hover:ring-cyan-400 transition cursor-pointer"
            >
              {isSubmitted ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">
                    Thank You!
                  </h3>
                  <p className="text-gray-300 mb-2">
                    Your consultation request has been submitted. We'll contact you within 24 hours.
                  </p>
                  <p className="text-sm text-blue-300">
                    📧 Check your email for confirmation details
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Full Name *
                    </label>
                    <input
                      {...register('name', { required: 'Name is required' })}
                      type="text"
                      className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter your full name"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-400">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Email Address *
                    </label>
                    <input
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                      type="email"
                      className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter your email"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Phone Number *
                    </label>
                    <input
                      {...register('phone', { required: 'Phone number is required' })}
                      type="tel"
                      className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter your phone number"
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-400">{errors.phone.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Course of Interest *
                    </label>
                    <select
                      {...register('course', { required: 'Please select a course' })}
                      className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select a course</option>
                      <option value="mbbs">MBBS</option>
                      <option value="engineering">Engineering</option>
                      <option value="scholarships">Scholarships</option>
                      <option value="other">Other</option>
                    </select>
                    {errors.course && (
                      <p className="mt-1 text-sm text-red-400">{errors.course.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Message
                    </label>
                    <textarea
                      {...register('message')}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Tell us about your requirements..."
                    />
                  </div>

                  {/* Referral Section */}
                  <div className="border-t border-gray-600 pt-6">
                    <h4 className="text-sm font-medium text-gray-300 mb-4 flex items-center">
                      <Star className="h-4 w-4 text-yellow-400 mr-2" />
                      Referral Information (Optional)
                    </h4>
                    
                    <div className="grid grid-cols-1 gap-4">
                      {/* Referral ID */}
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">
                          Referral ID
                        </label>
                        <input
                          {...register('referralId')}
                          type="text"
                          className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter referral ID"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          If you have a referral ID, enter it here
                        </p>
                      </div>

                      {/* Referral Name and Phone */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-1">
                            Referral Name
                          </label>
                          <input
                            {...register('referralName')}
                            type="text"
                            className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Who referred you?"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Name of the person who referred you
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-1">
                            Referral Phone
                          </label>
                          <input
                            {...register('referralPhone')}
                            type="tel"
                            className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Referrer's phone"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Phone number of your referrer
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 p-3 bg-blue-900/20 border border-blue-700/30 rounded-lg">
                      <p className="text-xs text-blue-300 flex items-start">
                        <Star className="h-3 w-3 text-yellow-400 mr-1 mt-0.5 flex-shrink-0" />
                        Get special benefits and discounts when you provide a valid referral! Our existing students and partners can refer you for exclusive offers.
                      </p>
                    </div>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    Submit Consultation Request
                  </motion.button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
    </UniversalFormWrapper>
  );
};

export default ConsultancyPage;